import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@atgis/ui";
import {
  CheckCircle2,
  GitBranch,
  GitMerge,
  History,
  Send,
  ShieldAlert,
  Sparkles,
} from "lucide-react";
import { SectionHeading } from "../components/SectionHeading";

const STEPS = [
  {
    icon: GitBranch,
    title: "Saha çalışma kopyası açılır",
    body: "Yönetici, Mevcut Altyapı'nın bir parçasını coğrafi sınır ve varlık filtresiyle koparır — saha ekibi için bir Altyapı Projesi hazırlanır. Saha bu kopya üzerinde çalışır; ana şebekeye dokunmaz.",
  },
  {
    icon: Send,
    title: "Revizyon onaya teslim edilir",
    body: "Saha işi bittiğinde Altyapı Projesi sahibi tek tıkla revizyonu kuruma teslim eder. Altyapı Projesi o andan itibaren onay bekler durumuna geçer; sahada başka değişiklik yapılmaz.",
  },
  {
    icon: GitMerge,
    title: "Kurum tarafında inceleme ve onay",
    body: "Mevcut Altyapı'da yetkili kullanıcı revizyonu inceler: hangi varlık eklendi, hangisi düzenlendi, hangi öznitelik değişti — hepsi öncesi-sonrası karşılaştırması olarak gösterilir. Birden fazla revizyon aynı bölgeyi etkilediyse oluşan çakışmalar bu adımda elle değerlendirilir; hiçbir saha verisi sessizce kaybolmaz. Yetkili kişi onaylar, düzeltme için geri gönderir veya reddeder.",
  },
  {
    icon: History,
    title: "Versiyonlu birleşme",
    body: "Onaylanan değişiklikler Mevcut Altyapı'ya yeni bir sürüm olarak işlenir. Hangi kullanıcının hangi tarihte hangi varlıkta ne değiştirdiği kayıt altında kalır — gerektiğinde geri alınabilir.",
  },
];

const ADVANTAGES = [
  {
    icon: ShieldAlert,
    title: "Saha hatası ana şebekeye sızmaz",
    body: "Yanlış işaretlenmiş bir vana, eksik bir branşman veya hatalı koordinat onaydan geçmeden ana şebekeye yazılmaz. Geleneksel CBS'lerde sahada yapılan her değişiklik anında canlıya gider — burada öyle değil.",
  },
  {
    icon: CheckCircle2,
    title: "Kurumsal denetim ve sorumluluk",
    body: "Her revizyonun teslim edeni, onaylayanı ve tarihi belli. Düzenleyici raporlar ve iç denetim için tam izlenebilirlik sunar — versiyon geçmişi inkâr edilemez.",
  },
  {
    icon: Sparkles,
    title: "Pazarda farkımız",
    body: "Mühendislik ofislerinin bildiği klasik revizyon-onay süreci, coğrafi veriye uygulanmış hâli. Türkiye'de ve Avrupa'da yaygın altyapı CBS ürünlerinin hiçbirinde bu seviyede onay zinciri yok — biz kurumsal süreci CBS'ye taşıdık.",
  },
];

export function Changeset() {
  return (
    <section
      id="changeset"
      className="relative overflow-hidden border-b border-border/60 bg-card/30"
    >
      <div className="container py-20 md:py-28">
        <SectionHeading
          eyebrow="Revizyon Onay Akışı"
          title={
            <>
              Sahadaki değişiklik, <span className="gradient-text">onaydan geçmeden</span> ana
              şebekeye yazılmaz.
            </>
          }
          description="Saha ekibi Mevcut Altyapı'ya doğrudan dokunmaz. Yapılan değişiklikler bir revizyon olarak toplanır, kuruma onaya sunulur, onaylandıktan sonra versiyonlanarak ana şebekeye işlenir. Bu kontrollü revizyon zinciri, RADGIS'i piyasadaki diğer altyapı CBS ürünlerinden ayıran en güçlü tasarım kararıdır."
        />

        <ChangesetFlow />

        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {ADVANTAGES.map(({ icon: Icon, title, body }) => (
            <Card key={title} className="h-full">
              <CardHeader>
                <div className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" />
                </div>
                <CardTitle className="mt-3 text-base">{title}</CardTitle>
                <CardDescription className="text-sm leading-relaxed">{body}</CardDescription>
              </CardHeader>
              <CardContent />
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function ChangesetFlow() {
  return (
    <div className="mt-14 overflow-hidden rounded-2xl border border-border/70 bg-background shadow-soft">
      <div className="grid gap-0 md:grid-cols-4">
        {STEPS.map(({ icon: Icon, title, body }, idx) => (
          <div
            key={title}
            className={
              "relative p-6 md:p-7 " +
              (idx > 0 ? "border-t border-border/60 md:border-t-0 md:border-l" : "")
            }
          >
            <div className="flex items-center gap-3">
              <div className="grid h-9 w-9 place-items-center rounded-lg bg-primary/10 text-primary">
                <Icon className="h-4.5 w-4.5" />
              </div>
              <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                Adım {idx + 1}
              </span>
            </div>
            <div className="mt-4 text-base font-semibold tracking-tight">{title}</div>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>

            {idx < STEPS.length - 1 ? (
              <div
                className="pointer-events-none absolute right-[-12px] top-1/2 hidden -translate-y-1/2 md:block"
                aria-hidden
              >
                <div className="grid h-6 w-6 place-items-center rounded-full border border-border bg-background text-muted-foreground">
                  <svg
                    viewBox="0 0 24 24"
                    className="h-3 w-3"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 12h14M13 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
